<template>
    <div class="card border-0 bg-white profile-settings-card rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
            <form v-on:submit="submit">
                <div class="row">
                    <!-- REVISION NUMBER -->
                    <div class="col-lg-8">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.revision_number.label') }}
                            </label>
                            <input v-model="version.revision_number" class="form-control shadow-none text-black" required
                                :disabled="published" :class="[{ 'is-invalid': submitted && errors?.revision_number }]"
                                type="text" pattern="^(0|(?:[1-9]\d*))\.(0|(?:[1-9]\d*))\.?(0|(?:[1-9]\d*))?" :placeholder="$t('form.revision_number.placeholder')"/>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.revision_number ?? false">
                                <p v-for="error in errors.revision_number" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                        error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- TITLE -->
                    <div class="col-lg-8">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.title.label') }}
                            </label>
                            <input v-model="version.title" class="form-control shadow-none text-black" required
                                :disabled="published" :class="[{ 'is-invalid': submitted && errors?.title }]" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.title ?? false">
                                <p v-for="error in errors.title" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- BODY -->
                    <div class="col-lg-8">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.body.label') }}
                            </label>
                            <div class="mb-0 quill-editor" :disabled="published">
                                <QuillEditor theme="snow" :toolbar="toolbar" v-model:content="version.body" contentType="html" ref="editor"
                                    :disabled="published" :readOnly="published" />
                            </div>
                            <!--  <textarea v-model="version.body" class="form-control shadow-none text-black"
                                :class="[{ 'is-invalid': submitted && errors?.body }]" rows="6"></textarea> -->
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.body ?? false">
                                <p v-for="error in errors.body" :key="error" class="small text-danger lh-1 m-0 mt-1">
                                    {{
                                        error }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- MANDATORY -->
                    <div class="col-12">
                        <div class="form-check form-group form-switch mb-15 mb-sm-20 mb-md-25">
                            <input class="form-check-input shadow-none me-8" type="checkbox" role="switch"
                                :disabled="published" v-model="version.mandatory" id="mandatory"
                                @change="switchReset('mandatory_message')">
                            <label class="form-check-label fw-medium position-relative top-1" for="mandatory">
                                {{ $t('form.mandatory.label') }}
                            </label>
                        </div>
                    </div>
                    <!-- MANDATORY -->
                    <div class="col-lg-8" v-if="version.mandatory">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <!-- <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.title.label') }}
                            </label> -->
                            <textarea v-model="version.mandatory_message" class="form-control shadow-none text-black"
                                :disabled="published" :placeholder="$t('form.mandatory.placeholder')"
                                :class="[{ 'is-invalid': submitted && errors?.mandatory_message }]" rows="6"></textarea>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.mandatory_message ?? false">
                                <p v-for="error in errors.mandatory_message" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                        error }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- HAS CHECKBOX -->
                    <div class="col-12">
                        <div class="form-check form-group form-switch mb-15 mb-sm-20 mb-md-25">
                            <input class="form-check-input shadow-none me-8" type="checkbox" role="switch"
                                :disabled="published" v-model="version.has_checkbox" id="has_checkbox"
                                @change="switchReset('checkbox_disclaimer')">
                            <label class="form-check-label fw-medium position-relative top-1" for="has_checkbox">
                                {{ $t('form.has_checkbox.label') }}
                            </label>
                        </div>
                    </div>
                    <!-- HAS CHECKBOX -->
                    <div class="col-lg-8" v-if="version.has_checkbox">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <!-- <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.title.label') }}
                            </label> -->
                            <textarea v-model="version.checkbox_disclaimer" class="form-control shadow-none text-black"
                                :disabled="published" :placeholder="$t('form.has_checkbox.placeholder')" required
                                :class="[{ 'is-invalid': submitted && errors?.checkbox_disclaimer }]" rows="6"></textarea>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.checkbox_disclaimer ?? false">
                                <p v-for="error in errors.checkbox_disclaimer" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                        error }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="col-12" v-if="loading">
                        <SpinnerLoading />
                    </div>
                    <div class="col-md-12" v-else>
                        <div class="d-flex align-items-center justify-content-between">
                            <!-- DISCARD -->
                            <button v-if="update && !published" type="button"
                                class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium" data-bs-toggle="modal"
                                data-bs-target="#discard_modal">
                                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                                <span class="position-relative">{{ $t('feature.contents.$blocks.discard_changes') }}</span>
                            </button>
                            <span v-else></span>
                            <!-- SAVE -->
                            <div class="d-flex align-items-center gap-3">
                                <button v-if="update && !published"
                                    class="default-btn transition outlined fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                                    type="submit" v-on:click="submitted = true; version.published = true">
                                    {{ $t('cta.publish_consent') }}
                                </button>
                                <button v-if="!published"
                                    class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                                    type="submit" v-on:click="submitted = true">
                                    {{ $t('cta.save_consent_version') }}
                                </button>
                                <button v-if="published"
                                    class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                                    @click="createVersion" type="button">
                                    {{ $t('cta.create_consent_version') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
    <!-- ERROR MESSAGE -->
    <div class="row justify-content-center">
        <div class="col">
            <div class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0"
                role="alert" id="version_error_message">
                <strong>{{ $t('error.something_went_wrong') }}</strong>
                <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
            </div>
        </div>
    </div>
    <ConfirmModal id="discard_modal" @confirm="discard()" :title="$t('cta.$delete.title')"
        :message="$t('cta.$delete.message')" />
</template>
      
<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import { Consent, ConsentVersion } from "@/utils/models/consent.model";
import { version } from "vue";
import { QuillEditor } from "@vueup/vue-quill";
import QUILL_TOOLBAR from "@/utils/const/quill_config";

export default defineComponent({
    name: "ConsentForm",
    components: {
        ConfirmModal,
        SpinnerLoading,
    },
    data: function () {
        return {
            source_version: { ...(((this.$route.meta.consent ?? {}) as Consent)?.last_consent_version ?? {}) as ConsentVersion },
            version: (this.$route.meta.consent_version as ConsentVersion | undefined) ?? (((this.$route.meta.consent ?? {}) as Consent)?.last_consent_version ?? {}) as ConsentVersion,
            submitted: false,
            loading: false,
            errors: undefined as any,
            toolbar: QUILL_TOOLBAR
        }
    },
    mounted() {
        if (this.restore_version !== undefined) {
            this.version.published_at = undefined;
        }
    },
    computed: {
        update(): boolean {
            return ((this.$route.meta.consent ?? {}) as Consent)?.last_consent_version !== undefined && ((this.$route.meta.consent ?? {}) as Consent)?.last_consent_version !== null;
        },
        published(): boolean {
            return this.version.published_at !== undefined;
        },
        restore_version(): ConsentVersion | undefined { return this.$route.meta.consent_version as ConsentVersion | undefined }
    },
    methods: {
        submit(event): void {
            this.errors = undefined;
            event.preventDefault();
            console.log("CONSENT", this.version);
            removeNullProperties<ConsentVersion>(this.version);

            this.loading = true;
            if (this.update) {
                console.log('UPDATE');
                API.updateConsent(this.version).then((response) => {
                    this.$router.push(`/consents`);
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false;
                });
            } else {
                console.log('CREATE');

                API.createConsent(this.version).then((response) => {
                    if (response.data?.data?.id !== undefined) {
                        this.$router.push(`/consent/${response.data.data.id}`);
                    } else {
                        this.$router.push(`/consents`);
                    }
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false;
                });
            }
        },
        /* deleteConsent(): void {
            this.loading = true;
            API.deleteConsent(this.version.id!).then((response) => {
                if (response.data.success === true) {
                    this.$router.push('/consents');
                }
            }).catch((error) => {
                this.showError();
            }).finally(() => {
                console.log("DELETED_CONSENT_ID", this.version.id!);
                this.loading = false;
            })
        }, */
        showError() {
            const error = document.getElementById('version_error_message');
            error?.classList.add('show');
            setTimeout(() => {
                this.hideError();
            }, 5000)
        },
        hideError() {
            const error = document.getElementById('version_error_message');
            error?.classList.remove('show');
        },
        switchReset(field: string) {
            if (this.version.hasOwnProperty(field)) {
                this.version[field] = undefined;
            }
        },
        createVersion() {
            /* CLEAN VERSION */
            // (this.$route.meta.consent as Consent).active_version = {} as ConsentVersion;
            /* LAST VERSION */
            (this.$route.meta.consent as Consent).last_consent_version = { ...this.source_version };
            this.version = { ...this.source_version };
            this.version.published_at = undefined;

            this.submitted = false;
            this.loading = false;
            this.errors = undefined;

            const editor = this.$refs.editor as InstanceType<typeof QuillEditor>;
            editor.reinit();
            editor.getQuill().enable(true);
        },
        discard() {
            (this.$route.meta.consent as Consent).last_consent_version = { ...this.source_version };
            this.version = { ...this.source_version };

            this.submitted = false;
            this.loading = false;
            this.errors = undefined;

            const editor = this.$refs.editor as InstanceType<typeof QuillEditor>;
            editor.reinit();
        }
    },

});
</script>